import { textStyleCss } from 'src/theme/atlas';
import styled from 'styled-components';

export const AccordionStyled = styled.div<{
  open: boolean;
  topBorder?: boolean;
  shadow?: boolean;
  topBorderRadius: string;
  bottomBorderRadius: string;
  showError?: boolean;
  bodyBackgroundColor?: string;
  containerBorderColor?: string;
}>`
  width: 100%;
  min-width: 620px;
  // overflow property is needed to avoid gaps at base of pages with lists of accordions
  overflow: ${(props) => (props.open ? 'visible' : 'hidden')};

  ${(props) =>
    props.topBorder &&
    `
      border-top-left-radius: ${props.topBorderRadius};
      border-top-right-radius: ${props.topBorderRadius};
      border-top: solid 1px ${
        props.showError
          ? props.theme.atlas.semantic.color.status.error.default
          : props.theme.atlas.semantic.color.outline.disabled
      };
  `}
  ${(props) =>
    !props.topBorder
      ? `:first-child > div:first-child {
    border-top-left-radius: ${props.topBorderRadius};
    border-top-right-radius: ${props.topBorderRadius};
    border-top: solid 1px
      ${
        props.showError
          ? props.theme.atlas.semantic.color.status.error.default
          : props.theme.atlas.semantic.color.outline.disabled
      };
  }`
      : ''}
  &:last-child > div:first-child {
    border-bottom-left-radius: ${(props) =>
      props.open ? '0px' : props.bottomBorderRadius};
    border-bottom-right-radius: ${(props) =>
      props.open ? '0px' : props.bottomBorderRadius};
  }
  ${(props) => props.shadow && `box-shadow: 0px 8px 24px -6px #00000029;`}
  border-bottom-left-radius: ${(props) => props.bottomBorderRadius};
  border-bottom-right-radius: ${(props) => props.bottomBorderRadius};
  background-color: ${(props) => props.bodyBackgroundColor};
  border: ${(props) =>
    props.containerBorderColor
      ? ` 1px solid ${props.containerBorderColor}`
      : 'none'};
`;

export const AccordionHeader = styled.div<{
  open: boolean;
  topBorderRadius: string;
  bottomBorderRadius: string;
  showError?: boolean;
  bottomBorder?: boolean;
  backgroundColor?: string;
  borderColor?: string;
  hoverColor?: string;
}>`
  background-color: ${(props) =>
    props.backgroundColor
      ? props.backgroundColor
      : props.showError
        ? props.theme.atlas.semantic.color.status.error.background
        : props.theme.atlas.semantic.color.surface.default};
  display: grid;
  grid-template-columns: ${(props) =>
    props.showError ? '2fr 2fr' : '3fr 1fr'};
  justify-content: space-between;
  border: ${(props) =>
    props.borderColor ? ` 1px solid ${props.borderColor}` : 'none'};
  gap: 20px;
  padding: 20px;
  cursor: pointer;
  position: relative;
  align-items: center;
  ${(props) =>
    props.borderColor
      ? ` 1px solid ${props.borderColor}`
      : props.bottomBorder
        ? `border-bottom: solid 1px
  ${
    props.showError
      ? props.theme.atlas.semantic.color.status.error.default
      : props.theme.atlas.semantic.color.outline.disabled
  }`
        : ''};
  border-left: solid 1px
    ${(props) =>
      props.backgroundColor
        ? ` 1px solid ${props.backgroundColor}`
        : props.showError
          ? props.theme.atlas.semantic.color.status.error.default
          : props.theme.atlas.semantic.color.outline.disabled};
  border-right: solid 1px
    ${(props) =>
      props.backgroundColor
        ? ` 1px solid ${props.backgroundColor}`
        : props.showError
          ? props.theme.atlas.semantic.color.status.error.default
          : props.theme.atlas.semantic.color.outline.disabled};
  &:hover {
    background-color: ${(props) =>
      props.hoverColor
        ? props.hoverColor
        : props.theme.atlas.semantic.color.surface.variant};
  }
  border-top-left-radius: ${(props) => props.topBorderRadius};
  border-top-right-radius: ${(props) => props.topBorderRadius};
  overflow-x: hidden;
  border-bottom-left-radius: ${(props) =>
    props.open ? '0px' : props.bottomBorderRadius};
  border-bottom-right-radius: ${(props) =>
    props.open ? '0px' : props.bottomBorderRadius};
`;

export const AccordionTitle = styled.h3<{ bottomMargin?: boolean }>`
  ${({ theme }) => textStyleCss(theme.atlas.typography.semantic.body)}
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  color: ${(props) => props.theme.atlas.semantic.color.type.default};
  margin: 0px;
  margin-bottom: ${(props) => (props.bottomMargin ? '10px' : '0px')};
`;

export const AccordionSubtitle = styled.p`
  ${({ theme }) => textStyleCss(theme.atlas.typography.semantic.bodySmall)}
  color: ${(props) => props.theme.atlas.semantic.color.type.muted};
  width: max-content;
  margin: 0px;
`;

export const IconWrapper = styled.div<{ open: boolean }>`
  transform: ${(props) => (props.open ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform 0.4s ease-in-out;
  padding: 0px;
`;

export const AccordionSubtitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  justify-self: end;
`;

export const AccordionContent = styled.div<{
  open: boolean;
  last?: boolean;
  bottomBorderRadius: string;
  backgroundColor?: string;
}>`
  max-height: ${(props) => (props.open ? `none` : '0px')};
  opacity: ${(props) => (props.open ? '1' : '0')};
  transition:
    max-height
      ${(props) =>
        props.open
          ? '0.4s cubic-bezier(0.25, 1, 0.5, 1)'
          : '0.4s 0.3s cubic-bezier(0.25, 1, 0.5, 1)'},
    padding ${(props) => (props.open ? '0s' : '0.3s')} ease-in-out,
    opacity ${(props) => (props.open ? '0.3s' : '0s')} ease-in-out;
  background-color: ${(props) =>
    props.backgroundColor || props.theme.atlas.semantic.color.surface.default};

  border-bottom-left-radius: ${(props) =>
    props.last ? props.bottomBorderRadius : '0px'};
  border-bottom-right-radius: ${(props) =>
    props.last ? props.bottomBorderRadius : '0px'};
`;

export const AccordionContentTitle = styled.h4`
  margin: 0 0 20px 0;
  color: ${(props) => props.theme.atlas.semantic.color.type.default};
  ${({ theme }) => textStyleCss(theme.atlas.typography.semantic.body)}
  font-weight: 600;
`;

export const AccordionContentContainer = styled.div<{
  first?: boolean;
  last?: boolean;
  borderRadius: string;
}>`
  margin: 0;
  padding: 20px;
  border-top: ${(props) =>
    props.first
      ? 'none'
      : `solid 1px ${props.theme.atlas.semantic.color.outline.disabled}`};
  border-bottom-left-radius: ${(props) =>
    props.last ? props.borderRadius : '0px'};
  border-bottom-right-radius: ${(props) =>
    props.last ? props.borderRadius : '0px'};
  &:hover {
    background-color: ${(props) =>
      props.theme.atlas.semantic.color.surface.subtle};
  }
`;

export const AccordionContentSubtitle = styled.h5`
  margin: 0 0 12px 0;
  color: ${(props) => props.theme.atlas.semantic.color.type.default};
  ${({ theme }) => textStyleCss(theme.atlas.typography.semantic.caption)}
`;

export const AccordionContentDescription = styled.p<{ margin?: boolean }>`
  margin: ${(props) => (props.margin ? '0 0 20px 0' : '0')};
  width: 50%;
  color: ${(props) => props.theme.atlas.semantic.color.type.muted};
  ${({ theme }) => textStyleCss(theme.atlas.typography.semantic.bodySmall)}
`;

export const AccordionContentWrapper = styled.div<{
  open: boolean;
  last?: boolean;
  bottomBorderRadius: string;
  hideContentBorder?: boolean;
}>`
  ${(props) =>
    !props.hideContentBorder &&
    `border-left: solid 1px ${props.theme.atlas.semantic.color.outline.disabled};
  border-right: solid 1px ${props.theme.atlas.semantic.color.outline.disabled};
  transition: border-bottom 0s ${props.open ? '0s' : '0.4s'};
  border-bottom: ${
    props.open
      ? `solid 1px ${props.theme.atlas.semantic.color.outline.disabled}`
      : `solid 0px ${props.theme.atlas.semantic.color.outline.disabled}`
  };
  border-bottom-left-radius: ${props.last ? props.bottomBorderRadius : '0px'};
  border-bottom-right-radius: ${
    props.last ? props.bottomBorderRadius : '0px'
  };`}
`;
